import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge must know our custom theme names (src/styles/theme.css), otherwise it thinks
 * `text-label` (a font SIZE) and `text-on-accent` (a COLOR) conflict and silently drops one.
 * Add new `--text-*`, `--radius-*` or `--shadow-*` theme keys here too.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: [
        'eyebrow',
        'label',
        'table',
        'body',
        'title',
        'section',
        'page',
        'brand',
        'kpi',
        'hero',
      ],
      radius: ['field', 'panel'],
      shadow: ['popover'],
    },
  },
});

/** Merge Tailwind classes; later classes win (shadcn/ui convention). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Tiny key-value storage interface so code that persists things can be tested without a browser. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * localStorage that never throws. Safari private mode, full disks and locked-down phones can
 * throw on any access; the planner must keep working (it just forgets preferences).
 */
export const safeLocalStorage: KeyValueStorage = {
  getItem(key) {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  setItem(key, value) {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      // ignore — see comment above
    }
  },
  removeItem(key) {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      // ignore
    }
  },
};

/** In-memory storage for tests. */
export function createMemoryStorage(initial: Record<string, string> = {}): KeyValueStorage {
  const map = new Map(Object.entries(initial));
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

/** Human message from anything thrown. */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong';
}
